import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import styled from "styled-components";

import { baseConfig } from "./../../../axiosConfig";
import { Context } from "./../../../contexts/Store";

import tickIconGreen from "../../../assets/icons/tick.svg";
import fileUploadIcon from "../../../assets/icons/file_upload.svg";
import closeIcon from "../../../assets/icons/close.png";
import reUploadIcon from "../../../assets/icons/re-upload-icon.svg";

import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../includes/loader/Loader";
import NormalSubmissionSubmittedFile from "./NormalSubmissionSubmittedFile";
import {
  formatFileSize,
  normalizeFileType,
  getAcceptedFileTypes,
  getFileIcon,
} from "./fileUtils";

// ============= UTILITY FUNCTIONS =============
const getFileTypeColor = (selectedFile, itemFileType) => {
  const normalizedSelected = normalizeFileType(selectedFile);
  const normalizedItem = normalizeFileType(itemFileType);

  if (normalizedSelected === "pdf" && normalizedItem === "pdf")
    return "#FFEBEE";

  if (normalizedSelected === "ppt" && normalizedItem === "ppt")
    return "#FFF3E0";

  if (normalizedSelected === "word" && normalizedItem === "word")
    return "#E3F2FD";

  if (normalizedSelected === "xlsx" && normalizedItem === "xlsx")
    return "#E7F5ED";

  if (
    ["jpg", "png", "gif", "webp", "svg", "image"].includes(
      normalizedSelected
    ) &&
    ["jpg", "png", "gif", "webp", "svg", "image"].includes(normalizedItem)
  )
    return "#E3F2FD";

  if (
    ["mp4", "video"].includes(normalizedSelected) &&
    ["mp4", "video"].includes(normalizedItem)
  )
    return "#F3E5F5";

  if (
    selectedFile.toLowerCase() === "similarity report" &&
    itemFileType.toLowerCase() === "similarity report"
  )
    return "#F3E5F5";

  return "#ffffff";
};

const LoadedFileDiv = React.memo(
  ({ file, setUploadedFiles, setFileArray, setSubmitButton }) => {
    const formattedSize = useMemo(
      () => formatFileSize(file.file?.size),
      [file.file?.size]
    );
    const extension = useMemo(
      () => file.file?.name.split(".").pop(),
      [file.file?.name]
    );

    const handleDelete = useCallback(() => {
      const fileExtension = file.file?.name.split(".").pop();
      const deletedFileType = normalizeFileType(fileExtension);

      setUploadedFiles((prev) =>
        prev.filter((item) => item.file !== file.file)
      );
      setSubmitButton(false);

      setFileArray((prev) => {
        return prev.map((item) => {
          const itemType = normalizeFileType(item.file_type);
          if (itemType === deletedFileType) {
            return { ...item, count: item.count + 1 };
          }
          return item;
        });
      });
    }, [file.file, setUploadedFiles, setSubmitButton, setFileArray]);

    return (
      <FileCard>
        <FileCardLink
          href={file.file ? URL.createObjectURL(file.file) : file.file_url}
          target="_blank"
        >
          <FileCardContent>
            <FileIcon src={getFileIcon(extension)} />
            <FileInfo>
              <FileName>{file.file?.name || file.file_url}</FileName>
              <FileMeta>
                {formattedSize && <FileSize>{formattedSize}</FileSize>}
                {file.date && <FileDate>{file.date}</FileDate>}
              </FileMeta>
            </FileInfo>
          </FileCardContent>
        </FileCardLink>
        {file.file?.size && (
          <DeleteButton>
            <FileIcon
              src={closeIcon}
              style={{ width: "13px", height: "13px", cursor: "pointer" }}
              onClick={handleDelete}
            />
          </DeleteButton>
        )}
      </FileCard>
    );
  }
);

const SubmitNormal = ({
  setSinglePage,
  refreshApi,
  assignmentDetails,
  fileArray,
  selectedFile,
  setFileArray,
  setSelectedFile,
}) => {
  const { state } = useContext(Context);
  const { id } = useParams();
  const navigate = useNavigate();

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitButton, setSubmitButton] = useState(false);
  const [similarityReport, setSimilarityReport] = useState({
    file: null,
    percentage: null,
    status: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);

  const submitAssignmentFile = useCallback(() => {
    let accessToken = state.user_details.access_token;
    const formdata = new FormData();

    uploadedFiles.forEach((item) => {
      formdata.append("submitted_file", item.file);
    });

    setIsLoading(true);
    baseConfig
      .post(
        `/students/assignment-submit/${id || assignmentDetails.assignment_id}/`,
        formdata,
        {
          headers: {
            Authorization: "Bearer " + accessToken,
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then((response) => {
        const { StatusCode } = response.data;
        setIsLoading(false);
        if (StatusCode === 6000) {
          setSinglePage && setSinglePage(false);
          refreshApi && refreshApi();
          if (!setSinglePage && !refreshApi) {
            navigate("/assignments/");
          }
        } else {
          alert("Contact LMS");
        }
      })
      .catch((error) => {
        if (error?.response?.data?.code === "token_not_valid") {
          localStorage.clear();
          window.location = "/";
        }
        setIsLoading(false);
        console.error("Submit error:", error);
        alert("Contact LMS");
      });
  }, [
    uploadedFiles,
    similarityReport,
    id,
    assignmentDetails,
    state.user_details.access_token,
    setSinglePage,
    refreshApi,
    navigate,
  ]);

  const handleFileSelection = useCallback(
    (type) => {
      setSelectedFile(type);
    },
    [setSelectedFile]
  );

  const handleReport = useCallback(
    async (e) => {
      let accessToken = state.user_details.access_token;
      const formData = new FormData();
      formData.append("submitted_file", e.target.files[0]);

      try {
        const { data } = await baseConfig.post(
          "/students/similarity-check/",
          formData,
          {
            headers: {
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        setSimilarityReport({
          file: e.target.files[0],
          status: data.data.status,
          percentage: data.data.percentage,
        });

        const totalFileCount = fileArray.reduce(
          (acc, item) => acc + Number(item.count),
          0
        );

        if (data.data.status === "valid") {
          setFileArray((prev) =>
            prev.map((item) => {
              const itemType = normalizeFileType(item.file_type);

              if (itemType === "Similarity Report") {
                return { ...item, count: Math.max(0, item.count - 1) };
              }
              return item;
            })
          );

          const now = new Date();
          const formattedTime = now.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          });

          const newFileObjects = {
            file: e.target.files[0],
            date: formattedTime,
          };

          setUploadedFiles((prev) => [...prev, newFileObjects]);

          const position = fileArray.findIndex(
            (item) => item.file_type === "Similarity Report"
          );
          if (position + 1 < fileArray.length) {
            setSelectedFile(fileArray[position + 1].file_type);
          }
        }

        if (totalFileCount <= 1) {
          setSubmitButton(true);
        } else {
          setSubmitButton(false);
        }
      } catch (error) {
        alert("Please upload the correct similarity report");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [
      state.user_details.access_token,
      fileArray,
      setFileArray,
      setSelectedFile,
      setUploadedFiles,
    ]
  );

  const handleFileUpload = useCallback(
    (e) => {
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }

      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const file = files[0];
      const fileExtension = file.name.split(".").pop();

      let fileDetails = fileArray.find((item) => {
        const normalizedItemType = normalizeFileType(item.file_type);
        const normalizedFileExt = normalizeFileType(fileExtension);

        return (
          normalizedItemType === normalizedFileExt ||
          item.file_type === fileExtension
        );
      });

      if (!fileDetails) {
        alert("No matching file details found for this file type.");
        return;
      }

      const existingFileNames = new Set(
        uploadedFiles.map((item) => item.file.name)
      );
      const duplicates = files.filter((file) =>
        existingFileNames.has(file.name)
      );

      if (duplicates.length > 0) {
        const duplicateFileNames = duplicates
          .map((file) => ` - ${file.name}`)
          .join("\n");
        alert(
          `The following file(s) have already been uploaded:\n${duplicateFileNames}`
        );
        e.target.value = null;
        return;
      }

      const dedicatedCountArray = uploadedFiles.filter((item) => {
        const uploadedExt = item.file.name.split(".").pop();
        return (
          normalizeFileType(uploadedExt) === normalizeFileType(fileExtension)
        );
      });

      if (
        (fileDetails.count % 2 === 0
          ? fileDetails.count >= dedicatedCountArray.length
          : fileDetails.count + 1 >= dedicatedCountArray.length) &&
        files.length <= fileDetails.count
      ) {
        const now = new Date();
        const formattedTime = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        });

        const newFileObjects = files.map((file) => ({
          file: file,
          date: formattedTime,
        }));

        setUploadedFiles((prev) => [...prev, ...newFileObjects]);

        setFileArray((prev) =>
          prev.map((item) => {
            const itemType = normalizeFileType(item.file_type);
            const uploadedType = normalizeFileType(fileExtension);

            if (itemType === uploadedType) {
              return { ...item, count: Math.max(0, item.count - files.length) };
            }
            return item;
          })
        );

        if (files.length === fileDetails.count) {
          const position = fileArray.findIndex(
            (item) => item.file_type === fileDetails.file_type
          );
          if (position + 1 < fileArray.length) {
            setSelectedFile(fileArray[position + 1].file_type);
          }
        }
      } else {
        alert(`Count of ${fileExtension} files exceeds the allowed limit.`);
      }

      const totalFileCount = fileArray.reduce(
        (acc, item) => acc + Number(item.count),
        0
      );

      if (!similarityReport.file ? totalFileCount <= 1 : totalFileCount <= 2) {
        if (
          (similarityReport.file && similarityReport.status === "valid") ||
          !similarityReport.file
        ) {
          setSubmitButton(true);
        } else {
          setSubmitButton(false);
        }
      } else {
        setSubmitButton(false);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [
      uploadedFiles,
      fileArray,
      similarityReport,
      setUploadedFiles,
      setFileArray,
      setSelectedFile,
    ]
  );

  const isDropZoneActive = useMemo(() => {
    const currentFileType = fileArray?.find(
      (item) => item.file_type === selectedFile
    );
    return (
      currentFileType?.count > 0 ||
      (selectedFile === "Similarity Report" &&
        similarityReport.status !== "valid")
    );
  }, [fileArray, selectedFile, similarityReport.status]);

  const handleDragEnter = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
    dragCounter.current++;

    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
    dragCounter.current--;

    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const files = Array.from(e.dataTransfer.files);

      const acceptedTypes = getAcceptedFileTypes(selectedFile);
      const validFiles = files.filter((file) => {
        const extension = "." + file.name.split(".").pop().toLowerCase();
        return acceptedTypes === "*/*" || acceptedTypes.includes(extension);
      });

      if (validFiles.length === 0) {
        alert(`Please drop ${acceptedTypes} files only`);
        return;
      }

      if (selectedFile === "Similarity Report") {
        const event = { target: { files: [validFiles[0]] } };
        handleReport(event);
      } else {
        const event = { target: { files: validFiles } };
        handleFileUpload(event);
      }
    },
    [selectedFile, handleReport, handleFileUpload]
  );

  return (
    <SubmissionSection>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {["Ongoing", "Returned", "Active"].includes(
            assignmentDetails?.status
          ) && <SubmissionText>SUBMIT ASSIGNMENT</SubmissionText>}

          {["Ongoing", "Closed", "Returned", "Active"].includes(
            assignmentDetails?.status
          ) ? (
            <div>
              <FileTypeSelection>
                <InnerFileTypeSelection>
                  {fileArray?.map((item) => (
                    <TabContainer
                      key={item.file_type}
                      selected={selectedFile === item.file_type}
                    >
                      <FileType
                        style={{
                          backgroundColor: getFileTypeColor(
                            selectedFile,
                            item.file_type
                          ),
                          minWidth:
                            selectedFile === item.file_type ? "160px" : "80px",
                        }}
                        onClick={() => handleFileSelection(item.file_type)}
                      >
                        {item.file_type}
                      </FileType>
                    </TabContainer>
                  ))}
                </InnerFileTypeSelection>
              </FileTypeSelection>

              <AssignmentSubmissionContainer>
                <>
                  <SubmittingContainer
                    style={{
                      cursor: isDropZoneActive ? "pointer" : "default",
                      backgroundColor: isDragging ? "#f0f8ff" : "transparent",
                      borderColor: isDragging ? "#4048AE" : "#9d9d9d",
                    }}
                    onDragEnter={isDropZoneActive ? handleDragEnter : undefined}
                    onDragLeave={isDropZoneActive ? handleDragLeave : undefined}
                    onDragOver={isDropZoneActive ? handleDragOver : undefined}
                    onDrop={isDropZoneActive ? handleDrop : undefined}
                  >
                    {isDragging && isDropZoneActive ? (
                      <DragActiveContent>
                        <FileIcon
                          style={{
                            width: "80px",
                            height: "80px",
                            opacity: 0.8,
                          }}
                          src={fileUploadIcon}
                          alt="file_upload_icon"
                        />
                        <DragText>Drop your files here</DragText>
                      </DragActiveContent>
                    ) : (
                      <>
                        <FileIcon
                          style={{ width: "50px", height: "65px" }}
                          src={fileUploadIcon}
                          alt="file_upload_icon"
                        />
                        <UploadMainText>
                          <span style={{ color: "#4048AE" }}>
                            Drag and Drop
                          </span>{" "}
                          your file here
                        </UploadMainText>

                        {fileArray?.find(
                          (item) => item.file_type === selectedFile
                        )?.count > 0 ? (
                          <FileCountDiv>
                            {selectedFile === "Similarity Report" &&
                            similarityReport.file === null ? (
                              <>
                                Drop your{" "}
                                <span style={{ color: "#DF0000" }}>PDF</span>{" "}
                                file here
                              </>
                            ) : selectedFile === "Similarity Report" &&
                              similarityReport.file !== null &&
                              similarityReport.status === "invalid" ? (
                              <InvalidReportContainer>
                                <ReuploadSection>
                                  <div style={{ color: "#007BFF" }}>
                                    reupload
                                  </div>
                                  <FileIcon
                                    src={reUploadIcon}
                                    alt="re-upload icon"
                                    style={{
                                      height: "15px",
                                      width: "15px",
                                      marginLeft: "3px",
                                    }}
                                  />
                                </ReuploadSection>
                                <ErrorMessage>
                                  The similarity index exceeds 10%. Please
                                  re-upload the assignment files.
                                </ErrorMessage>
                              </InvalidReportContainer>
                            ) : selectedFile === "Similarity Report" &&
                              similarityReport.file !== null &&
                              similarityReport.status === "valid" ? (
                              <CompletedSection>
                                <CompletedText>completed</CompletedText>
                                <FileIcon
                                  src={tickIconGreen}
                                  alt="tick"
                                  style={{
                                    height: "15px",
                                    width: "15px",
                                    marginLeft: "3px",
                                  }}
                                />
                              </CompletedSection>
                            ) : (
                              <>
                                (Drop {selectedFile.charAt(0).toUpperCase()}
                                {selectedFile.slice(1)} File X
                                {
                                  fileArray?.find(
                                    (item) => item.file_type === selectedFile
                                  )?.count
                                }
                                )
                              </>
                            )}
                          </FileCountDiv>
                        ) : (
                          <CompletedSection>
                            <CompletedText>completed</CompletedText>
                            <FileIcon
                              src={tickIconGreen}
                              alt="tick"
                              style={{
                                height: "15px",
                                width: "15px",
                                marginLeft: "3px",
                              }}
                            />
                          </CompletedSection>
                        )}

                        {(fileArray?.find(
                          (item) =>
                            item.file_type === selectedFile &&
                            selectedFile !== "Similarity Report"
                        )?.count > 0 ||
                          (selectedFile === "Similarity Report" &&
                            similarityReport.status !== "valid")) && (
                          <UploadButton>
                            <UploadButtonText>Choose file</UploadButtonText>
                            <input
                              ref={fileInputRef}
                              name="myImage"
                              type="file"
                              id="file1"
                              accept={getAcceptedFileTypes(selectedFile,fileArray)}
                              style={{ display: "none" }}
                              multiple={selectedFile !== "Similarity Report"}
                              onChange={
                                selectedFile !== "Similarity Report"
                                  ? handleFileUpload
                                  : handleReport
                              }
                            />
                          </UploadButton>
                        )}
                      </>
                    )}
                  </SubmittingContainer>

                  <LoadedFilesDiv>
                    <FilesGrid>
                      {uploadedFiles.map((item, index) => (
                        <LoadedFileDiv
                          key={`${item.file.name}-${index}`}
                          file={item}
                          setUploadedFiles={setUploadedFiles}
                          setFileArray={setFileArray}
                          setSubmitButton={setSubmitButton}
                        />
                      ))}
                    </FilesGrid>
                    <SubmitButtonContainer>
                      <SubmitButton
                        style={{
                          backgroundColor: submitButton
                            ? "#917AFD"
                            : "#828282ff",
                        }}
                        disabled={!submitButton}
                        onClick={() => submitAssignmentFile()}
                      >
                        Submit
                      </SubmitButton>
                    </SubmitButtonContainer>
                  </LoadedFilesDiv>
                </>
              </AssignmentSubmissionContainer>
            </div>
          ) : (
            <EmptyStateContainer></EmptyStateContainer>
          )}

          {assignmentDetails?.submitted_files?.length > 0 && (
            <NormalSubmissionSubmittedFile
              submittedFiles={assignmentDetails?.submitted_files}
            />
          )}
        </>
      )}
    </SubmissionSection>
  );
};

const SubmitButtonContainer = styled.div`
  margin-right: 10px;
  display: flex;
  justify-content: center;
  margin-top: 20px;
  align-items: start;
`;

const SubmissionSection = styled.div`
  margin-top: 30px;
`;

const SubmissionText = styled.p`
  text-transform: uppercase;
  font-size: 16px;
  color: #9d9d9d;
  margin-top: 30px;
  margin-bottom: 20px;
`;

const SubmitButton = styled.button`
  display: inline-block;
  height: 40px;
  padding: 12px 20px;
  background: #917afd;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.05rem;
  color: #fff;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #8069e8;
    box-shadow: 0 4px 8px rgba(145, 122, 253, 0.3);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const AssignmentSubmissionContainer = styled.div`
  border: 1px solid #9d9d9d;
  margin-top: -1px;
  border-radius: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  padding: 20px 30px;
`;

const FileTypeSelection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;
const FileCard = styled.div`
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.25);
  min-width: 220px;
  height: 70px;
  border-radius: 8px;
  display: flex;
  padding: 10px;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.3);
  }
`;

const FileCardLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  flex-direction: column;
  flex: 1;
`;

const FileCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  flex: 1;
`;

const FileInfo = styled.div``;

const FileName = styled.div`
  font-size: 15px;
  font-weight: bold;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileMeta = styled.div`
  display: flex;
`;

const FileSize = styled.div`
  font-size: 12px;
  margin-right: 10px;
`;

const FileDate = styled.div`
  font-size: 12px;
`;

const DeleteButton = styled.div`
  width: 20%;
  display: flex;
  justify-content: end;
  align-items: start;
`;

const InnerFileTypeSelection = styled.div`
  min-width: 50%;
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const CompletedSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #09c425;
`;

const TabContainer = styled.div`
  border: ${(props) => (props.selected ? "1px solid #9d9d9d" : "none")};
  border-bottom: ${(props) => (props.selected ? "2px solid white" : "none")};
  border-radius: ${(props) => (props.selected ? "14px 14px 0 0" : "0")};
  padding: ${(props) => (props.selected ? "5px 5px 0 5px" : "0")};
`;

const FileType = styled.div`
  max-width: 270px;
  height: 45px;
  padding: 12px;
  text-align: center;
  color: #313131;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const FileCountDiv = styled.div`
  color: #797979;
  text-align: center;
  font-size: 18px;
  line-height: 160%;
`;

const LoadedFilesDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-top: 20px;
`;

const FilesGrid = styled.div`
  width: 100%;
  gap: 10px;
  display: flex;
  flex-wrap: wrap;
`;

const SubmittingContainer = styled.label`
  border: 2px dashed #9d9d9d;
  box-sizing: border-box;
  border-radius: 10px;
  width: 100%;
  height: 350px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
  margin-bottom: 20px;
  transition: all 0.3s ease;
`;

const DragActiveContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const DragText = styled.div`
  font-size: 24px;
  color: #4048ae;
  font-weight: bold;
  margin-top: 20px;
`;

const UploadMainText = styled.div`
  font-size: 22.25px;
  line-height: 160%;
`;

const UploadButton = styled.label`
  padding: 12px 10px;
  border-radius: 9px;
  width: 120px;
  background: #e9e9e9;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #d4d4d4;
  }
`;

const UploadButtonText = styled.p`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: -2px;
  color: #000000;
`;

const FileIcon = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
  margin-right: 8px;
`;

const InvalidReportContainer = styled.div`
  text-align: center;
`;

const ReuploadSection = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  color: red;
  margin-bottom: 2px;
`;

const CompletedText = styled.div`
  color: #09c425;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 160%;
  letter-spacing: 0.72px;
`;

const EmptyStateContainer = styled.div`
  width: 100%;
  text-align: center;
`;
export default SubmitNormal;
